import {projectConversationEvent, type ConversationProjectionEvent} from './conversation-event-projection';
import {mergeGenerationProgress} from '../../../../shared/playbookGenerationProgress';
import {applyCloneProgress} from './canvas-clone-progress';
import {cancelledDeliverySummary} from '../../../../shared/playbookCancellation';
import {reconcileRecoveredRunCards} from './recovered-run-cards';
import type { CanvasChatMessage, CanvasImage, ChatImageSlot, PlaybookProgressState } from './canvas-types';

export interface PlaybookJobView {
  id: number;
  projectId: number;
  assistantMessageId: string;
  kind?: string;
  status: 'queued' | 'running' | 'paused' | 'completed' | 'failed' | 'cancelled';
  position?: number | null;
  activeCount?: number;
  limit?: number;
  cancellationRequested?: boolean;
  error?: string | null;
  workflowRunIds?: number[];
  enqueuedAt?: string;
  result?: unknown;
  deliveryReceipt?: unknown;
  lastStatus?: string;
}
export interface PlaybookJobEvent { id: number; event: { type: string; data?: any } }
export const isSettledJob = (job: PlaybookJobView) => ['completed', 'failed', 'cancelled'].includes(job.status);
export const isWaitingForAdmission = (message: CanvasChatMessage) => !!message.playbookJobs?.some(job => job.status === 'queued') && !message.playbookJobs.some(job => job.status === 'running');
/** A saved result can reach chat independently of the canvas replay cursor. */
export function latestCompletedDelivery(messages: readonly CanvasChatMessage[]) {
  for (let index = messages.length - 1; index >= 0; index--) {
    const message = messages[index];
    if (message.role === 'assistant'
      && (message.playbookProgress?.isDone || message.playbookJobs?.some(job => job.status === 'completed'))
      && message.imageSlots?.some(slot => !slot.failed && !!slot.resolved)) return message;
  }
  return undefined;
}

export function missingCompletedDelivery(
  message: CanvasChatMessage | undefined,
  images: readonly Pick<CanvasImage, 'id' | 'src'>[],
): boolean {
  if (!message) return false;
  const visible = images.filter(image => !!image.src);
  const ids = new Set(visible.map(image => image.id));
  const sources = new Set(visible.map(image => image.src));
  return !!message.imageSlots?.some(slot => !slot.failed && !!slot.resolved
    && !sources.has(slot.resolved) && (!slot.requestId || !ids.has(slot.requestId)));
}

const LIVE_STREAM_IDLE_MS = 30_000;
const liveStreams = new Map<string, {lastReadAt: number; detach?: () => void}>();
const pendingReconciliation = new WeakSet<Map<number, number>>();
const lastEventPageJob = new WeakMap<Map<number, number>, number>();
export function setPlaybookStreamActive(projectId: number, messageId: string, active: boolean, detach?: () => void) {
  const key = `${projectId}:${messageId}`;
  if (active) liveStreams.set(key, {lastReadAt: Date.now(), detach}); else liveStreams.delete(key);
}
export function touchPlaybookStream(projectId: number, messageId: string, now = Date.now()) {
  const stream = liveStreams.get(`${projectId}:${messageId}`);
  if (stream) stream.lastReadAt = now;
}
export function hasLivePlaybookStream(projectId: number, messageId: string, now = Date.now()) {
  const key = `${projectId}:${messageId}`;
  const stream = liveStreams.get(key);
  if (!stream) return false;
  if (now - stream.lastReadAt < LIVE_STREAM_IDLE_MS) return true;
  liveStreams.delete(key);
  // Only the browser transport is detached. The admitted server job continues
  // and its recorded events take over; this never submits or cancels work.
  stream.detach?.();
  return false;
}
const owns = (job: PlaybookJobView, projectId: number) => job.projectId === projectId && Number.isSafeInteger(job.id) && job.id > 0 && !!job.assistantMessageId;

/** Job identity, not the currently open chat or last message, owns every update. */
export function mergePlaybookJobs(messages: CanvasChatMessage[], jobs: PlaybookJobView[], projectId: number): CanvasChatMessage[] {
  let next = messages;
  for (const job of jobs.filter(job => owns(job, projectId))) {
    const index = next.findIndex(message => message.id === job.assistantMessageId && message.role === 'assistant');
    const message: CanvasChatMessage = index < 0 ? { id: job.assistantMessageId, role: 'assistant', content: '', isAgentMessage: true, isStreaming: false } : next[index];
    const previous = message.playbookJobs?.find(item => item.id === job.id);
    // An older polling response cannot turn an admitted/completed job back into a queue entry.
    if (previous && ((isSettledJob(previous) && !isSettledJob(job)) || (previous.status === 'running' && job.status === 'queued'))) continue;
    const updated = { ...previous, ...job, lastStatus: job.lastStatus ?? previous?.lastStatus };
    const playbookJobs = [...(message.playbookJobs ?? []).filter(item => item.id !== job.id), updated];
    const replacement = { ...message, playbookJobs, playbookJobVerifiedAt:playbookJobs.some(item=>item.status==='running')?Date.now():undefined, content: message.content.startsWith('_(Previous turn didn\'t complete') ? '' : message.content };
    next = index < 0 ? [...next, replacement] : next.map((item, i) => i === index ? replacement : item);
  }
  return reconcileRecoveredRunCards(next);
}

const integer = (value: unknown): value is number => Number.isSafeInteger(value) && (value as number) >= 0;
const eventRunId = (job: PlaybookJobView, data: any): number | undefined => integer(data.workflowRunId) ? data.workflowRunId : job.workflowRunIds?.length === 1 ? job.workflowRunIds[0] : undefined;
const slotRunId = (slot: ChatImageSlot): number | undefined => slot.workflowRunId ?? (slot.conceptKey && /^\d+:/.test(slot.conceptKey) ? Number(slot.conceptKey.split(':')[0]) : undefined);
const ownsProgress = (progress: PlaybookProgressState, job: PlaybookJobView, runId?: number) => progress.workflowRunId == null
  ? !(job.workflowRunIds && job.workflowRunIds.length > 1)
  : runId != null ? progress.workflowRunId === runId : !!job.workflowRunIds?.includes(progress.workflowRunId);

function replayImageSlot(slots: ChatImageSlot[], job: PlaybookJobView, type: string, data: any): ChatImageSlot[] {
  const runId = eventRunId(job, data);
  const requestId = typeof data.requestId === 'string' && data.requestId ? data.requestId : undefined;
  const index = [data.conceptIndex, data.index, data.slotIndex].find(integer);
  const compatible = (slot: ChatImageSlot) => runId == null || slotRunId(slot) == null || slotRunId(slot) === runId;
  let position = requestId ? slots.findIndex(slot => slot.requestId === requestId && compatible(slot)) : -1;
  if (position < 0 && index != null) position = slots.findIndex(slot => slot.index === index && compatible(slot) && (!slot.requestId || slot.requestId === requestId));
  const ready = ['canvas_image_ready', 'variation_complete', 'style_complete'].includes(type) && typeof data.imageUrl === 'string' && !!data.imageUrl;
  if (position < 0 && ready) position = slots.findIndex(slot => slot.resolved === data.imageUrl && compatible(slot) && (!requestId || !slot.requestId || slot.requestId === requestId));
  // Old saved messages have unlabelled pending slots. Recorded pending events
  // claim them once, so out-of-order ready events can then use request identity.
  if (position < 0 && (type === 'canvas_image_pending' || ready) && index == null) position = slots.findIndex(slot => !slot.resolved && !slot.requestId && compatible(slot));
  if (position < 0 && !ready && type !== 'canvas_image_pending') return slots;
  const previous = position >= 0 ? slots[position] : {index: index != null && !slots.some(slot => slot.index === index) ? index : Math.max(-1, ...slots.map(slot => slot.index)) + 1};
  if (previous.resolved && !ready) return slots;
  const updated: ChatImageSlot = {...previous, ...(requestId ? {requestId} : {}), ...(runId != null ? {workflowRunId: runId} : {}),
    ...(typeof data.previewUrl==='string'&&data.previewUrl.startsWith('https://')?{previewUrl:data.previewUrl}:{}),
    ...(typeof data.aspectRatio === 'string' ? {aspectRatio: data.aspectRatio} : {}),
    ...(ready ? {resolved: data.imageUrl, failed: false, errorMsg: undefined, failure: undefined, statusText: undefined} :
      type === 'canvas_image_failed' ? {failed: true, errorMsg: data.error ?? data.reason ?? 'This image could not finish.', failure: data.failure, statusText: undefined} :
      typeof data.statusText === 'string' ? {statusText: data.statusText} : {})};
  return (position < 0 ? [...slots, updated] : slots.map((slot, i) => i === position ? updated : slot))
    .filter(slot => slot === updated || !ready || slot.resolved !== updated.resolved || !!slot.requestId || !compatible(slot))
    .sort((a, b) => a.index - b.index);
}

/** Replay only recorded progress and delivered assets; queued jobs never invent slots. */
export function replayPlaybookJobEvents(messages: CanvasChatMessage[], job: PlaybookJobView, events: PlaybookJobEvent[], projectId: number, options: {terminalReady?: boolean} = {}): CanvasChatMessage[] {
  if (!owns(job, projectId)) return messages;
  const terminalReady = options.terminalReady !== false;
  messages = events.reduce((rows, {event}) => integer(event.data?.workflowRunId) && job.workflowRunIds?.length && !job.workflowRunIds.includes(event.data.workflowRunId)
    ? rows : rows.map(message => projectConversationEvent(message, job.assistantMessageId, event)), messages);
  return reconcileRecoveredRunCards(messages.map(message => {
    if (message.id !== job.assistantMessageId || message.role !== 'assistant') return message;
    let next = { ...message };
    for (const { event } of events) {
      const data = event.data ?? {};
      const runId = eventRunId(job, data);
      if (integer(data.workflowRunId) && job.workflowRunIds?.length && !job.workflowRunIds.includes(data.workflowRunId)) continue;
      if(['step','variation_progress','variation_complete','done','error'].includes(event.type)&&next.agentTaskGroups?.some(group=>group.id.startsWith('clone-'))) {
        if (terminalReady || !['done','error'].includes(event.type)) next={...next,agentTaskGroups:next.agentTaskGroups.map(group=>group.id.startsWith('clone-')?applyCloneProgress(group,event.type,data):group)};
      }
      if (['canvas_image_pending','canvas_image_progress','canvas_image_failed','canvas_image_ready','variation_complete','style_complete'].includes(event.type)) next = {...next, imageSlots: replayImageSlot(next.imageSlots ?? [], job, event.type, data)};
      if (event.type === 'playbook_start' && (!next.playbookProgress || ownsProgress(next.playbookProgress, job, runId))) {
        const phases = Array.isArray(data.phases) ? data.phases : [];
        next = {...next, playbookProgress: next.playbookProgress ?? {workflowRunId: runId, playbookName: data.playbookName ?? 'Campaign', totalPhases: data.totalPhases ?? phases.length, phases,
          phaseStatuses: Object.fromEntries(phases.map((phase: any) => [phase.id, 'pending'])), phaseStatusTexts: {}, activePhaseId: null, qaResults: [], conceptProgress: null, completedSummary: null, isDone: false}};
      }
      if (next.playbookProgress && ownsProgress(next.playbookProgress, job, runId)) {
        const progress = next.playbookProgress;
        if (event.type === 'playbook_phase_start' && data.phaseId && !progress.isDone) next = {...next, playbookProgress: {...progress, activePhaseId: data.phaseId, phaseStatuses: {...progress.phaseStatuses, [data.phaseId]: 'running'}}};
        if (event.type === 'playbook_phase_progress' && !progress.isDone) next = {...next, playbookProgress: {...progress, phaseStatusTexts: {...progress.phaseStatusTexts, ...(data.phaseId && data.statusText ? {[data.phaseId]: data.statusText} : {})}, conceptProgress: mergeGenerationProgress(progress.conceptProgress, data)}};
        if (event.type === 'playbook_phase_complete' && data.phaseId) next = {...next, playbookProgress: {...progress, phaseStatuses: {...progress.phaseStatuses, [data.phaseId]: data.success === false ? 'failed' : 'completed'}, activePhaseId: progress.activePhaseId === data.phaseId ? null : progress.activePhaseId}};
        if (event.type === 'playbook_qa_result') next = {...next, playbookProgress: {...progress, qaResults: [...progress.qaResults.filter(result => result.conceptIndex !== data.conceptIndex), {conceptIndex: data.conceptIndex ?? 0, attempt: data.attempt ?? 1, score: data.score ?? 0, passed: data.passed ?? false, status: data.status ?? 'needs_review'}]}};
        if (event.type === 'playbook_complete') next = {...next, playbookProgress: {...progress, ...(terminalReady ? {isDone: true, activePhaseId: null} : {}), completedSummary: data.summary ?? progress.completedSummary}};
      }
      if (event.type === 'agent_done' && typeof data.text === 'string' && data.text.trim()) next = { ...next, content: data.text };
      const status = event.type === 'playbook_phase_progress' ? data.statusText : event.type === 'playbook_phase_start' ? data.phaseName : event.type === 'agent_thinking' ? data.text : undefined;
      if (typeof status === 'string' && status.trim()) next = { ...next, playbookJobs: next.playbookJobs?.map(item => item.id === job.id ? { ...item, lastStatus: status } : item) };
    }
    if (isSettledJob(job) && terminalReady) {
      // The initial model reply is an admission acknowledgement. Once the
      // single owned job settles, its durable outcome replaces that stale
      // prose even when no images were admitted. No text matching or new call.
      const finalMessage = (job.result as {message?:unknown}|undefined)?.message ?? (job.kind==='conversation_tool'&&job.status==='failed'?`I couldn’t finish this request.${job.error?' '+job.error:''}`:undefined);
      if (job.kind === 'conversation_tool' && next.playbookJobs?.length === 1 && typeof finalMessage === 'string' && finalMessage.trim()) {
        next = {...next, content:finalMessage};
      }
      const ownedSlots = (next.imageSlots ?? []).filter(slot => slotRunId(slot) != null ? !!job.workflowRunIds?.includes(slotRunId(slot)!) : !next.playbookJobs?.some(other => other.id !== job.id));
      const missing = ownedSlots.filter(slot => !slot.resolved);
      // A cancelled job can have delivered an already accepted image. Only its
      // counted receipt and matching ready slots may replace this job's prose.
      const cancelledSummary = job.status === 'cancelled' && next.playbookJobs?.length === 1 ? cancelledDeliverySummary(job.result) : undefined;
      const generated = (job.result as {generated?: Array<{id:string;imageUrl:string}>} | undefined)?.generated;
      if (cancelledSummary && ownedSlots.filter(slot => !!slot.resolved).length === generated?.length && generated?.every(image => ownedSlots.some(slot => slot.requestId === image.id && slot.resolved === image.imageUrl))) next = {...next, content: cancelledSummary};
      if (missing.length) next = {...next, imageSlots: next.imageSlots?.map(slot => missing.includes(slot) ? {...slot, failed: true, statusText: undefined, errorMsg: slot.errorMsg ?? job.error ?? 'This image was not delivered before the run ended.'} : slot)};
      if (next.playbookProgress && ownsProgress(next.playbookProgress, job)) {
        const failed = job.status !== 'completed' || missing.length > 0;
        const summary = missing.length ? `${ownedSlots.length - missing.length} of ${ownedSlots.length} ads delivered. ${missing.length} could not be delivered.` : job.error ?? next.playbookProgress.completedSummary;
        next = {...next, playbookProgress: {...next.playbookProgress, isDone: true, activePhaseId: null, phaseStatusTexts: {}, completedSummary: summary, phaseStatuses: Object.fromEntries(Object.entries(next.playbookProgress.phaseStatuses).map(([id, status]) => [id, ['pending','running'].includes(status) ? failed ? 'failed' : 'completed' : status]))}};
      }
      if (!next.playbookJobs?.some(other => other.id !== job.id && !isSettledJob(other)) && /^The live connection dropped, but your ads are still generating\b/.test(next.content)) {
        const delivered = ownedSlots.filter(slot => !!slot.resolved).length;
        const delivery = missing.length ? `${delivered} of ${ownedSlots.length} ads delivered.` : `${delivered} ad${delivered === 1 ? '' : 's'} delivered.`;
        next = {...next, content: `${delivery}${next.playbookProgress?.completedSummary ? ` ${next.playbookProgress.completedSummary}` : job.error ? ` ${job.error}` : ''}`};
      }
      if (next.agentTaskGroups) next={...next,agentTaskGroups:next.agentTaskGroups.map(group=>group.id.startsWith('clone-')?applyCloneProgress(group,job.status==='completed'?'done':'error',{}):group)};
    }
    if ((!isSettledJob(job) || terminalReady) && !next.playbookJobs?.some(other => other.id !== job.id && !isSettledJob(other))) next = {...next, isStreaming: false, agentThinking: undefined};
    return next;
  }));
}

export async function pollPlaybookJobsOnce(options: {
  projectId: number; cursors: Map<number, number>; settledJobs?: Set<number>;
  request?: typeof fetch;
  signal?: AbortSignal;
  getMessages: () => CanvasChatMessage[];
  writeMessages: (update: (messages: CanvasChatMessage[]) => CanvasChatMessage[]) => void;
  reconcile: (signal?: AbortSignal) => Promise<void>;
  isCurrent: () => boolean;
  onEvents?: (events: ConversationProjectionEvent[]) => void;
  onQueueRead?: (jobs: PlaybookJobView[]) => void;
  maxEventPages?: number;
}): Promise<void> {
  if (!options.isCurrent()) return;
  // Release a previously admitted stalled transport before queuing more HTTP
  // reads behind it. Unrelated conversational streams have no durable job and
  // are never detached by this recovery loop.
  for (const message of options.getMessages()) {
    if (message.playbookJobs?.some(job => owns(job, options.projectId) && job.status === 'running')) {
      hasLivePlaybookStream(options.projectId, message.id);
    }
  }
  const request = options.request ?? fetch;
  const response = await request(`/api/playbook-jobs?projectId=${options.projectId}`, {signal: options.signal});
  if (!response.ok || !options.isCurrent()) return;
  const payload = await response.json();
  const jobs: PlaybookJobView[] = Array.isArray(payload.jobs) ? payload.jobs.filter((job: PlaybookJobView) => owns(job, options.projectId)) : [];
  if (!options.isCurrent()) return;
  options.onQueueRead?.(jobs);
  options.writeMessages(messages => mergePlaybookJobs(messages, jobs, options.projectId));
  let reconcile = pendingReconciliation.has(options.cursors);
  const terminal: PlaybookJobView[] = [];
  let eventPages=0;
  const maxEventPages=Number.isSafeInteger(options.maxEventPages)&&options.maxEventPages!>0?options.maxEventPages!:Infinity;
  const previousIndex=jobs.findIndex(job=>job.id===lastEventPageJob.get(options.cursors));
  const replayOrder=previousIndex<0?jobs:[...jobs.slice(previousIndex+1),...jobs.slice(0,previousIndex+1)];
  for (const job of replayOrder) {
    if (!options.isCurrent()) return;
    if (options.settledJobs?.has(job.id) || job.status === 'queued') continue;
    if (!isSettledJob(job) && hasLivePlaybookStream(options.projectId, job.assistantMessageId)) {
      // The live stream owns chat/event projection, but ready events only
      // retire its canvas placeholders. Hydrate canonical images while it is
      // still open so a long batch cannot show thumbnails over an empty canvas.
      reconcile = true;
      pendingReconciliation.add(options.cursors);
      continue;
    }
    if(eventPages>=maxEventPages)break;
    eventPages++;
    lastEventPageJob.set(options.cursors,job.id);
    const eventsResponse = await request(`/api/playbook-jobs/${job.id}/events?after=${options.cursors.get(job.id) ?? 0}`, {signal: options.signal});
    if (!eventsResponse.ok || !options.isCurrent()) continue;
    const replay = await eventsResponse.json();
    if (!options.isCurrent()) return;
    if (replay.job && (!owns(replay.job, options.projectId) || replay.job.id !== job.id || replay.job.assistantMessageId !== job.assistantMessageId)) continue;
    const events: PlaybookJobEvent[] = Array.isArray(replay.events) ? replay.events : [];
    const latest: PlaybookJobView = replay.job && owns(replay.job, options.projectId) ? replay.job : job;
    options.writeMessages(messages => replayPlaybookJobEvents(mergePlaybookJobs(messages, [latest], options.projectId), latest, events, options.projectId, {terminalReady: false}));
    options.onEvents?.(events.map(({event}) => ({...event,data:{...event.data,assistantMessageId:job.assistantMessageId}})));
    if (Number.isSafeInteger(replay.nextCursor)) options.cursors.set(job.id, replay.nextCursor);
    if (events.some(({ event }) => ['canvas_image_ready', 'canvas_image_pending', 'variation_complete', 'style_complete', 'playbook_complete', 'canvas_images_reload'].includes(event.type))) {
      reconcile = true;
      pendingReconciliation.add(options.cursors);
    }
    if (isSettledJob(latest) && events.length < 500) {
      reconcile = true; // A failed planner may settle after preview with no final image event.
      terminal.push(latest);
    }
  }
  if (reconcile && options.isCurrent()) {
    await options.reconcile(options.signal);
    if (!options.isCurrent()) return;
    pendingReconciliation.delete(options.cursors);
    for (const job of terminal) {
      options.writeMessages(messages => replayPlaybookJobEvents(messages, job, [], options.projectId));
      options.onEvents?.([{type:'canvas_job_settled',data:{assistantMessageId:job.assistantMessageId,jobId:job.id,status:job.status,error:job.error,undoImageIds:(job.result as {undoImageIds?:string[]}|undefined)?.undoImageIds}}]);
      options.settledJobs?.add(job.id);
      setPlaybookStreamActive(options.projectId, job.assistantMessageId, false);
    }
  }
}

/** A queued fetch or response body cannot stop every subsequent recovery poll. */
export async function pollPlaybookJobsWithDeadline(options: Parameters<typeof pollPlaybookJobsOnce>[0], timeoutMs = 15_000): Promise<void> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  const timer = setTimeout(abort, timeoutMs);
  options.signal?.addEventListener('abort', abort, {once: true});
  if (options.signal?.aborted) abort();
  let onAbort: () => void = () => {};
  const aborted = new Promise<never>((_, reject) => {
    onAbort = () => reject(new DOMException('Canvas recovery read interrupted', 'AbortError'));
    controller.signal.addEventListener('abort', onAbort, {once: true});
    if (controller.signal.aborted) onAbort();
  });
  try {
    await Promise.race([aborted, pollPlaybookJobsOnce({...options, signal: controller.signal,
      isCurrent: () => !controller.signal.aborted && options.isCurrent(),
    })]);
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener('abort', abort);
    controller.signal.removeEventListener('abort', onAbort);
  }
}
